import React, { useMemo } from 'react';
import { Timeline, Empty, Spin } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useClientLastMessage, useClientActivitiesFromDate } from '../../../../../hooks/query/useEvents';

/**
 * Parse date from various formats
 * Handles ISO 8601, Unix timestamps, and YYYYMMDDHHMMSS format
 */
const parseDate = dateValue => {
  if (!dateValue) return null;

  // If it's already a dayjs object or Date
  if (dayjs.isDayjs(dateValue) || dateValue instanceof Date) {
    return dayjs(dateValue);
  }

  // If it's a string in ISO 8601 format (contains T or -)
  if (typeof dateValue === 'string' && (dateValue.includes('T') || dateValue.includes('-'))) {
    return dayjs(dateValue);
  }

  // If it's a number, check format
  if (typeof dateValue === 'number') {
    // Check if it's YYYYMMDDHHMMSS format (14 digits, starts with 19XX or 20XX)
    // Example: 20250521154243 = May 21, 2025 at 15:42:43
    const numStr = dateValue.toString();
    if (numStr.length === 14 && (numStr.startsWith('19') || numStr.startsWith('20'))) {
      const year = numStr.substring(0, 4);
      const month = numStr.substring(4, 6);
      const day = numStr.substring(6, 8);
      const hour = numStr.substring(8, 10);
      const minute = numStr.substring(10, 12);
      const second = numStr.substring(12, 14);
      return dayjs(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    }
    // Unix timestamp in seconds (10 digits) or milliseconds (13 digits)
    if (dateValue > 1000000000000) {
      return dayjs(dateValue); // milliseconds
    }
    if (dateValue > 1000000000) {
      return dayjs(dateValue * 1000); // seconds, convert to ms
    }
  }

  // If it's a string of digits (like YYYYMMDDHHMMSS)
  if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
    // Check if it looks like YYYYMMDDHHMMSS (14 digits)
    if (dateValue.length === 14) {
      const year = dateValue.substring(0, 4);
      const month = dateValue.substring(4, 6);
      const day = dateValue.substring(6, 8);
      const hour = dateValue.substring(8, 10);
      const minute = dateValue.substring(10, 12);
      const second = dateValue.substring(12, 14);
      return dayjs(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
    }
    // YYYYMMDD (8 digits)
    if (dateValue.length === 8) {
      const year = dateValue.substring(0, 4);
      const month = dateValue.substring(4, 6);
      const day = dateValue.substring(6, 8);
      return dayjs(`${year}-${month}-${day}`);
    }
    // Try parsing as number (could be Unix timestamp as string)
    const numValue = parseInt(dateValue, 10);
    if (numValue > 1000000000000) {
      return dayjs(numValue);
    }
    if (numValue > 1000000000) {
      return dayjs(numValue * 1000);
    }
  }

  // Fallback: let dayjs try to parse it
  return dayjs(dateValue);
};

/**
 * AudienceExpandedRow component for displaying client activity timeline
 * Lazy loads the last message and activities when the row is expanded
 */
const AudienceExpandedRow = ({ clientId, eventCreatedAt }) => {
  // Lazy load last outbound message
  const { data: messageData, isLoading: loadingMessage } = useClientLastMessage({
    clientId,
    enabled: !!clientId,
  });

  // Lazy load activities from event creation date
  const { data: activitiesData, isLoading: loadingActivities } = useClientActivitiesFromDate({
    clientId,
    fromDate: eventCreatedAt,
    enabled: !!clientId && !!eventCreatedAt,
  });

  // Get the last message from the response
  const messages = messageData?.data || messageData || [];
  const lastMessage = Array.isArray(messages) ? messages[0] : null;

  const isLoading = loadingMessage || loadingActivities;

  // Get icon for activity type
  const getActivityIcon = type => {
    const iconMap = {
      message: ['fal', 'comment'],
      store_visit: ['fal', 'store'],
      purchase: ['fal', 'shopping-bag'],
      appointment: ['fal', 'calendar-check'],
      note: ['fal', 'sticky-note'],
      call: ['fal', 'phone'],
      email: ['fal', 'envelope'],
      default: ['fal', 'circle'],
    };

    // Handle non-string types (object, number, etc.)
    const typeStr = typeof type === 'string' ? type.toLowerCase() : '';
    return iconMap[typeStr] || iconMap.default;
  };

  // Format activity date
  const formatDate = date => {
    const parsed = parseDate(date);
    if (!parsed || !parsed.isValid()) return '';
    return parsed.format('MMMM D, YYYY');
  };

  // Get the activity label based on type
  const getActivityLabel = activityType => {
    // Handle non-string types (object, number, etc.)
    const typeStr = typeof activityType === 'string' ? activityType : '';
    const typeLower = typeStr.toLowerCase();

    switch (typeLower) {
      case 'message':
        return 'sent a message';
      case 'store_visit':
        return 'added a Store Visit Activity';
      case 'purchase':
        return 'made a purchase';
      case 'appointment':
        return 'scheduled an appointment';
      case 'note':
        return 'added a note';
      case 'call':
        return 'logged a call';
      case 'email':
        return 'sent an email';
      case 'added to clientbook':
        return 'was added to Clientbook';
      default:
        // For custom activity types, use the original type name
        return `added "${typeStr || 'activity'}" activity`;
    }
  };

  // Get associate name from the message's user object
  const getAssociateName = msg => {
    // The /messages endpoint returns a user object with the associate info
    if (msg?.user?.name) return msg.user.name;

    // Fallback to other possible properties
    if (msg?.associateName) return msg.associateName;
    if (msg?.associate?.name) return msg.associate.name;
    if (msg?.senderName) return msg.senderName;
    if (msg?.sender?.name) return msg.sender.name;

    return 'Store';
  };

  // Combine message and activities into timeline items
  const timelineItems = useMemo(() => {
    const items = [];

    // Add last message if available
    if (lastMessage) {
      const senderName = getAssociateName(lastMessage);
      const messageDate = lastMessage.sentDateTime || lastMessage.createdAt;
      const messageBody = lastMessage.body || lastMessage.message || lastMessage.text || '';

      items.push({
        key: `message-${lastMessage.id || 'last'}`,
        type: 'message',
        date: messageDate,
        dot: <FontAwesomeIcon icon={getActivityIcon('message')} />,
        children: (
          <div className="timeline-item-content">
            <div>
              <span className="timeline-actor">{senderName}</span> sent a message on{' '}
              <span className="timeline-date">{formatDate(messageDate)}</span>
            </div>
            {messageBody && <div className="timeline-description">{messageBody}</div>}
          </div>
        ),
      });
    }

    // Add activities
    const activities = activitiesData?.data || activitiesData || [];
    if (Array.isArray(activities)) {
      activities.forEach((activity, index) => {
        // Actor name - check createdBy object first (API returns createdBy.name)
        const actorName =
          activity.createdBy?.name ||
          activity.createdByName ||
          activity.associate?.name ||
          activity.associateName ||
          activity.actorName ||
          'Unknown';
        // Type can be an object with name property or a string
        const activityType =
          (typeof activity.type === 'object' ? activity.type?.name : activity.type) ||
          activity.activityType ||
          'activity';
        const activityDate = activity.createdDate || activity.createdAt || activity.date;
        const description = activity.notes || activity.note || activity.description || activity.message || '';

        items.push({
          key: activity.id || `activity-${index}`,
          type: activityType,
          date: activityDate,
          dot: <FontAwesomeIcon icon={getActivityIcon(activityType)} />,
          children: (
            <div className="timeline-item-content">
              <div>
                <span className="timeline-actor">{actorName}</span> {getActivityLabel(activityType)} on{' '}
                <span className="timeline-date">{formatDate(activityDate)}</span>
              </div>
              {description && <div className="timeline-description">{description}</div>}
            </div>
          ),
        });
      });
    }

    // Sort by date descending (most recent first)
    items.sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateB.valueOf() - dateA.valueOf();
    });

    return items;
  }, [lastMessage, activitiesData]);

  if (isLoading) {
    return (
      <div className="audience-expanded-row loading">
        <Spin size="small" />
      </div>
    );
  }

  if (timelineItems.length === 0) {
    return (
      <div className="audience-expanded-row">
        <Empty description="No recent activity" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  return (
    <div className="audience-expanded-row">
      <Timeline items={timelineItems} />
    </div>
  );
};

AudienceExpandedRow.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  eventCreatedAt: PropTypes.string,
};

AudienceExpandedRow.defaultProps = {
  clientId: null,
  eventCreatedAt: null,
};

export default AudienceExpandedRow;
